import {
  Accordion,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  List,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { Fragment, useState } from "react";
import { useEntitlements, usePermission } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import type { ModuleKey } from "@how/types";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  useReturnFocus,
} from "@how/ui";
import { RoleEditorModal } from "./RoleEditorModal";
import { SYSTEM_ROLES, LEVEL_LABEL, areaLabel, extraLabel, permissionLabel } from "./role-catalog";
import { TeamTabs } from "./TeamTabs";
import {
  describeTeamError,
  useCustomRoles,
  useDeleteCustomRole,
  type CustomRole,
} from "./team-queries";

const NO_MODULES: ReadonlySet<ModuleKey> = new Set();

function holdersText(n: number): string {
  return n === 0
    ? "Nobody holds it yet"
    : n === 1
      ? "Held by 1 person"
      : `Held by ${String(n)} people`;
}

/**
 * The church's roles (ADR 0054): the twelve built-in roles, read-only, each with what it allows; then the roles the
 * church built from a level per area. users.read sees them; users.manage (owners and administrators) builds, edits
 * and deletes. A role someone holds cannot be deleted, and the screen says so before anyone tries.
 */
export function RolesPage() {
  const roles = useCustomRoles();
  const entitlements = useEntitlements();
  const canManage = usePermission("users.manage", { write: true });
  const remove = useDeleteCustomRole();
  const focus = useReturnFocus();
  // undefined: closed. null: building a new role. A role: editing it.
  const [editing, setEditing] = useState<CustomRole | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<CustomRole | null>(null);
  const manage = canManage.data === true;

  const openEditor = (role: CustomRole | null) => {
    focus.remember();
    setEditing(role);
  };
  const closeEditor = () => {
    setEditing(undefined);
    focus.restore();
  };

  return (
    <>
      <PageHeader
        title="Team"
        description="Who can sign in to your church's dashboard, and what each of them can do."
        actions={
          manage ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                openEditor(null);
              }}
            >
              New role
            </Button>
          ) : undefined
        }
      />
      <TeamTabs active="roles" />

      <Stack gap="lg">
        <Card>
          <Stack gap="sm">
            <Title order={2} size="h4">
              Your church's roles
            </Title>
            <Text size="sm" c="dimmed">
              Build a role when a built-in one gives someone too much or too little: a name, and a
              level for each area.
            </Text>
            {roles.isPending ? (
              <LoadingState rows={3} label="Loading your roles" />
            ) : roles.isError ? (
              <ErrorState
                message="Could not load your church's roles."
                onRetry={() => {
                  void roles.refetch();
                }}
              />
            ) : roles.data.roles.length === 0 ? (
              <EmptyState
                title="No custom roles yet"
                description="Build one when a built-in role gives too much or too little."
              />
            ) : (
              <Stack gap={0}>
                {roles.data.roles.map((role, index) => (
                  <Fragment key={role.id}>
                    {index === 0 ? null : <Divider my="sm" />}
                    <CustomRoleRow
                      role={role}
                      canManage={manage}
                      onEdit={() => {
                        openEditor(role);
                      }}
                      onDelete={() => {
                        focus.remember();
                        setDeleting(role);
                      }}
                    />
                  </Fragment>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>

        <Card>
          <Stack gap="sm">
            <Title order={2} size="h4">
              Built-in roles
            </Title>
            <Text size="sm" c="dimmed">
              Every church starts with these. They cannot be changed; open one to see what it
              allows.
            </Text>
            <Accordion variant="separated" chevronPosition="right">
              {SYSTEM_ROLES.map((role) => (
                <Accordion.Item key={role.key} value={role.key}>
                  <Accordion.Control aria-label={`${role.name}: view permissions`}>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm" fw={600}>
                        {role.name}
                      </Text>
                      {role.isPrivileged ? (
                        <Badge variant="light" color={semanticColor("warning")} size="sm">
                          Administration
                        </Badge>
                      ) : null}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {role.description}
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <List size="sm" spacing={2}>
                      {role.permissions.map((key) => (
                        <List.Item key={key}>{permissionLabel(key)}</List.Item>
                      ))}
                    </List>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Stack>
        </Card>
      </Stack>

      <RoleEditorModal
        key={editing === undefined ? "closed" : editing === null ? "new" : editing.id}
        role={editing}
        entitled={entitlements.data ?? NO_MODULES}
        onClose={closeEditor}
      />
      <ConfirmActionModal
        opened={deleting !== null}
        onClose={() => {
          setDeleting(null);
          focus.restore();
        }}
        title={`Delete ${deleting?.name ?? "this role"}?`}
        message={`${deleting?.name ?? "This role"} will be removed from your church. Nobody holds it, so nobody loses access. This cannot be undone; the deletion is in the audit log.`}
        confirmLabel="Delete role"
        danger
        loading={remove.isPending}
        onConfirm={() => {
          if (deleting === null) return;
          const target = deleting;
          remove.mutate(target.id, {
            onSuccess: () => {
              setDeleting(null);
              focus.restore();
              notifications.show({
                color: semanticColor("success"),
                title: "Role deleted",
                message: `${target.name} has been removed.`,
              });
            },
            onError: (e) => {
              setDeleting(null);
              focus.restore();
              notifications.show({
                color: semanticColor("danger"),
                title: "Role not deleted",
                message: describeTeamError(e),
              });
            },
          });
        }}
      />
    </>
  );
}

interface CustomRoleRowProps {
  readonly role: CustomRole;
  readonly canManage: boolean;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

function CustomRoleRow({ role, canManage, onEdit, onDelete }: CustomRoleRowProps) {
  const sheet = Object.entries(role.levels).filter(([, level]) => level !== "none");
  return (
    <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm" data-role={role.key}>
      <Stack gap={6} style={{ flex: "1 1 320px", minWidth: 0 }}>
        <Text component="h3" size="sm" fw={600} m={0}>
          {role.name}
        </Text>
        {role.description.length > 0 ? (
          <Text size="sm" c="dimmed">
            {role.description}
          </Text>
        ) : null}
        <Group gap={6} wrap="wrap">
          {sheet.map(([area, level]) => (
            <Badge key={area} variant="light" color="gray" size="sm">
              {areaLabel(area)}: {LEVEL_LABEL[level]}
            </Badge>
          ))}
          {role.extras.map((extra) => (
            <Badge key={extra} variant="light" color={semanticColor("warning")} size="sm">
              {extraLabel(extra)}
            </Badge>
          ))}
        </Group>
        <Text size="xs" c="dimmed">
          {holdersText(role.holders)}
          {canManage && role.holders > 0
            ? ". To delete it, give its holders another role on the Team members tab first."
            : null}
        </Text>
      </Stack>
      {canManage ? (
        <Group gap="xs" wrap="nowrap">
          <Button
            variant="default"
            size="xs"
            leftSection={<IconPencil size={14} />}
            aria-label={`Edit ${role.name}`}
            onClick={onEdit}
            style={{ flexShrink: 0 }}
          >
            Edit
          </Button>
          <Button
            variant="default"
            size="xs"
            leftSection={<IconTrash size={14} />}
            aria-label={
              role.holders > 0 ? `Delete ${role.name}: not while it is held` : `Delete ${role.name}`
            }
            disabled={role.holders > 0}
            title={role.holders > 0 ? "Give its holders another role first" : undefined}
            onClick={onDelete}
            style={{ flexShrink: 0 }}
          >
            Delete
          </Button>
        </Group>
      ) : null}
    </Group>
  );
}
